import React, { useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { formatPct, formatPf, formatPnl, formatR, formatRisk } from './format';

const CELLS = [
  { key: 'netPnl', label: 'Net P&L', format: (s) => formatPnl(s.netPnl), tone: 'net', span: true },
  { key: 'tradeCount', label: 'Trades', format: (s) => String(s.tradeCount ?? 0) },
  {
    key: 'maxRisk',
    label: 'Max risk',
    format: (s) => formatRisk(s.maxRisk),
    hint: 'Largest dollar amount at risk on any one trade, from qty × stop distance × point value.',
  },
  { key: 'winRate', label: 'Win rate', format: (s) => formatPct(s.winRate) },
  { key: 'profitFactor', label: 'Profit factor', format: (s) => formatPf(s.profitFactor) },
  { key: 'maxDrawdown', label: 'Max drawdown', format: (s) => formatPnl(-Math.abs(s.maxDrawdown || 0)), tone: 'dd', span: true },
];

const DETAILS = [
  {
    key: 'avgR',
    label: 'Average R',
    hint: 'Mean R-multiple of all closed trades. 1R is the distance from entry to stop.',
    format: (s) => formatR(s.avgR),
  },
  {
    key: 'avgWinR',
    label: 'Average winner R',
    hint: 'Mean R-multiple of trades that finished profitable.',
    format: (s) => formatR(s.avgWinR),
  },
  {
    key: 'avgLossR',
    label: 'Average loser R',
    hint: 'Mean R-multiple of trades that finished at a loss.',
    format: (s) => formatR(s.avgLossR),
  },
  {
    key: 'avgWin',
    label: 'Average win',
    hint: 'Mean P&L of trades that finished profitable.',
    format: (s) => formatPnl(s.avgWin),
  },
  {
    key: 'avgLoss',
    label: 'Average loss',
    hint: 'Mean P&L of trades that finished at a loss.',
    format: (s) => formatPnl(s.avgLoss),
  },
];

export default function BacktestStats({ stats, stale = false }) {
  const { colors } = useTheme();
  const [open, setOpen] = useState(false);

  return (
    <div
      data-testid="backtest-stats"
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 1,
        background: colors.border,
        border: `1px solid ${colors.border}`,
        borderRadius: 10,
        overflow: 'hidden',
        opacity: stale ? 0.55 : 1,
      }}
    >
      {CELLS.map((cell) => {
        const value = stats ? cell.format(stats) : '—';
        let color = colors.text;
        if (stats && cell.tone === 'net') {
          color = stats.netPnl > 0 ? colors.success : stats.netPnl < 0 ? colors.danger : colors.text;
        }
        if (cell.tone === 'dd' && stats?.maxDrawdown) color = colors.danger;
        return (
          <div
            key={cell.key}
            title={cell.hint}
            style={{
              padding: '10px 12px',
              background: colors.bgCard,
              gridColumn: cell.span ? '1 / -1' : undefined,
            }}
          >
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: colors.textMuted }}>
              {cell.label}
            </div>
            <div style={{ fontSize: 18, fontWeight: 650, fontVariantNumeric: 'tabular-nums', color, marginTop: 2 }}>
              {value}
            </div>
          </div>
        );
      })}
      {stats ? (
        <div
          style={{
            gridColumn: '1 / -1',
            background: colors.bgCard,
          }}
        >
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              padding: '8px 12px',
              border: 'none',
              background: 'transparent',
              color: colors.textMuted,
              fontSize: 11,
              fontWeight: 650,
              cursor: 'pointer',
            }}
          >
            Averages
            {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
          {open ? (
            <div data-testid="backtest-stats-details" style={{ padding: '0 12px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {DETAILS.map((row) => (
                <OverlayTrigger
                  key={row.key}
                  placement="left"
                  delay={{ show: 0, hide: 0 }}
                  overlay={(
                    <Tooltip id={`backtest-avg-tip-${row.key}`}>
                      {row.hint}
                    </Tooltip>
                  )}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 8,
                      fontSize: 12,
                      color: colors.text,
                      cursor: 'help',
                    }}
                  >
                    <span>{row.label}</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 650 }}>{row.format(stats)}</span>
                  </div>
                </OverlayTrigger>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
